/**
 * A Drop's stored configuration -> the slots a structure can position.
 *
 * This is the join between what an operator authored (`Drop.sections[]`,
 * `Drop.contentBlocks[]`) and what the structure layer understands (a role and
 * a footprint). It is the only place that knows both vocabularies, and it must
 * stay the only place — a structure that learned about `DropSectionType` would
 * have to learn about every future host type too.
 *
 * ## The mapping is read off the anchors, not invented
 *
 * `publisher-drop-hub.html` is Modern Warfare 4 in hub form, and the live
 * `modern-warfare-4` Drop is the same title in page form with four sections
 * (ENGAGEMENT, STREAMING, NEWS, GAMES). Laying those two side by side is what
 * decides where each section lands:
 *
 *   the section list itself   -> the left rail (the anchor's `Nav · sections`)
 *   COLLECTION_TYPES          -> a horizontal rail of peer cards
 *   everything else           -> a full-width band
 *
 * `COLLECTION_TYPES` is the list below rather than a copy of it here — an
 * enumeration in a header is a second source of truth that drifts the moment
 * the set changes, which is exactly what happened when this comment named
 * three of its six members.
 *
 * The stage is emitted separately and unconditionally, because it is not one
 * of the sections — see below.
 *
 * ## The stage is not a section, and STREAMING is not the stage
 *
 * This file used to hand the stage to the STREAMING section. That produced a
 * surface where the "Creator Team" section appeared in the rail and rendered
 * nowhere — it had been consumed — and it made the stage's existence depend on
 * an operator having arranged a particular section.
 *
 * The stage carries the surface's IDENTITY: the lockup, the phase, the
 * synopsis, the CTA. It becomes the viewer when something is selected from
 * anywhere on the page. A surface with no sections at all still has one,
 * because it still has a name. So it is emitted unconditionally and built from
 * the Drop, and STREAMING takes the rail every other set of peers takes.
 */

import {SurfaceSlot, SlotRole, SlotFootprint} from "./slots";

/** The subset of a Drop section this needs. Kept minimal so it survives schema growth. */
export interface MappableSection {
    _id?: string;
    id?: string;
    type?: string;
    title?: string;
    enabled?: boolean;
    order?: number;
    /** What an ENGAGEMENT section points at — LEDGER_BOARD routes to the column. */
    engagementType?: string;
}

/**
 * Section types that are a scannable set of peers rather than a statement.
 * These take a horizontal rail so the viewer moves ALONG them; as a band they
 * would wrap into a grid and stop reading as a set.
 */
/*
 * STREAMING belongs here, not on the stage.
 *
 * It was the STAGE_TYPE, so a Drop's "Creator Team" section was swallowed by
 * the stage and never rendered as a set anywhere — the rail listed it and
 * nothing was behind it. Two things were wrong with that. The section is a
 * roster, which is a scannable set of peers and therefore a rail like any
 * other. And the stage is NOT a section: it is the surface's identity, built
 * from the Drop itself, so it must not depend on which sections an operator
 * happens to have arranged.
 */
/*
 * LEADERBOARDS STAYS HERE, and the reasoning for moving it was wrong.
 *
 * The two placement vocabularies do disagree — a board authored as a SECTION
 * takes a rail here, while the same board as a CONTENT BLOCK takes the column
 * (`CONTENT_BLOCK_SLOT.LEADERBOARD`). FLUIDITY_CONTRACT §7 backs the column.
 * Moving the section to FEATURE/COLUMN to match it was tried and reverted,
 * because it made the board render LESS often than the mismatch did:
 *
 *   · a section's `order` is >= 1, and `dropSlots` emits the featured slot at
 *     `order: -1`. `resolveOccupancy` takes `candidateIds[0]` with no priority
 *     logic, so featured wins the contested column by construction;
 *   · a COLUMN candidate that loses is not rendered anywhere — `HubStructure`
 *     builds its bands from BAND/HORIZONTAL_RAIL only, so the loser is dropped
 *     from the page rather than relocated;
 *   · `hasFeatured` is true for any Drop with a roster member or a trailer,
 *     i.e. nearly all of them.
 *
 * So the section silently vanished on the normal case, where before it always
 * drew as a rail. And even on a win it would be the wrong shape: the section
 * renders `ChallengesSection`, a hero-driven full section, not the compact
 * paged board §7 specifies for a 400px column.
 *
 * Two things have to exist before this move is correct, and neither does:
 * a leaderboard-shaped section renderer, and a `resolveOccupancy` that
 * implements the priority `HubSpine.scss:398-399` already documents —
 * "standings > chat > featured". Until then the mismatch is the safer bug.
 */
const COLLECTION_TYPES = new Set([
    "GAMES", "EXCLUSIVES", "TOURNAMENTS", "LEADERBOARDS", "CHALLENGES", "STREAMING",
    /*
     * NEWS is a set of peers too. As a BAND it fell through to the page's own
     * renderer and stacked full-width cards down the surface, while the modes
     * strip beside it scrolled — the same content shape rendering two
     * different ways on one page.
     */
    "NEWS",
]);

/**
 * Build the slot list for a Drop.
 *
 * @param sections   the Drop's sections, in authored order
 * @param opts.nav   emit a rail slot carrying section navigation (hub style
 *                   wants it; a page whose nav is a horizontal strip does not)
 */
export function dropSlots(
    sections: readonly MappableSection[] | null | undefined,
    opts: { nav?: boolean; featured?: boolean; modes?: boolean } = {},
): SurfaceSlot[] {
    const live = (sections ?? []).filter(s => s.enabled !== false);
    const slots: SurfaceSlot[] = [];

    /*
     * The rail is not one of the sections — it is a view OF them, which is why
     * it carries no section id. Emitted only when asked, because absence is a
     * configuration and a page-style surface navigates differently.
     */
    if (opts.nav && live.length > 0) {
        slots.push({id: "nav", role: SlotRole.NAV, footprint: SlotFootprint.RAIL, order: -1});
    }

    /*
     * FEATURED — the right column's default occupant.
     *
     * Like the rail, it is not one of the sections: it is what the surface has
     * to show beside the stage right now, which the anchor fills with the live
     * creator and the trailer. It is a COLUMN footprint, so it becomes a
     * candidate the occupancy resolver can hand the column to, and it loses
     * that column to a live leaderboard or to chat without either of them
     * knowing it exists.
     *
     * Emitted only when the caller says there is something to feature. A column
     * holding an empty card is the same failure the rail had — a correctly
     * sized box with nothing inside it, which reads as broken rather than as
     * absent.
     */
    /*
     * THE STAGE IS ALWAYS THERE, and it is not one of the sections.
     *
     * It carries the surface's identity — lockup, phase, synopsis, the CTA —
     * and becomes the viewer when something is selected. A surface with no
     * sections at all still has a stage, because it still has a name.
     */
    slots.push({id: "stage", role: SlotRole.STAGE, footprint: SlotFootprint.STAGE, order: 0});

    if (opts.featured) {
        slots.push({id: "featured", role: SlotRole.FEATURE, footprint: SlotFootprint.COLUMN, order: -1});
    }

    /*
     * MODES — "what ships in the box".
     *
     * Also not a section: it is a field on the surface (`modes[]`), authored
     * separately from the sections an operator arranges. It takes the same
     * HORIZONTAL_RAIL footprint as any other set of peers, which is what makes
     * it a strip rather than a bespoke block — the anchor's commerce rail and
     * journey rail are the same shape for the same reason.
     *
     * Ordered ahead of the section bands because it describes the thing the
     * surface is ABOUT, and the sections are what you can do on it.
     */
    if (opts.modes) {
        slots.push({
            id: "modes",
            role: SlotRole.COLLECTION,
            footprint: SlotFootprint.HORIZONTAL_RAIL,
            order: 0.5,
        });
    }

    live.forEach((s, i) => {
        const id = s._id ?? s.id ?? `section-${i}`;
        const type = s.type ?? "";

        /*
         * A LEDGER_BOARD section is the contested column's standings occupant
         * (FLUIDITY_CONTRACT §7). The two preconditions the LEADERBOARDS note
         * below records are both met for THIS vocabulary — `LedgerBoard` is
         * the column-shaped renderer, and `columnRank` implements
         * "standings > chat > featured" — so the new engagement type takes
         * the column while the legacy section type keeps its rail.
         */
        if (s.engagementType === "LEDGER_BOARD") {
            slots.push({
                id, role: SlotRole.FEATURE,
                footprint: SlotFootprint.COLUMN,
                order: s.order ?? i + 1,
                columnRank: 0,
            });
            return;
        }

        if (COLLECTION_TYPES.has(type)) {
            slots.push({
                id, role: SlotRole.COLLECTION,
                footprint: SlotFootprint.HORIZONTAL_RAIL,
                order: s.order ?? i + 1,
            });
            return;
        }


        slots.push({
            id, role: SlotRole.STATEMENT,
            footprint: SlotFootprint.BAND,
            order: s.order ?? i + 1,
        });
    });

    return slots;
}

/** The subset of a content block this needs. Kept minimal so it survives schema growth. */
export interface MappableContentBlock {
    _id?: string;
    id?: string;
    type?: string;
    order?: number;
    enabled?: boolean;
    /**
     * The `GamificationType` a `GAMIFICATION` block wraps — NOT stored on the
     * block (`SurfaceGamificationConfig` stores only `gamificationModelId`, a
     * reference). The serving route denormalizes the referenced model's
     * `type` onto `gamificationConfig.gamificationType` on read
     * (`resolveGamificationTypes`), which is what makes the
     * `GAMIFICATION_SLOT` routing below LIVE: `DropHubSurface` passes
     * `drop.contentBlocks` straight through and the field arrives resolved.
     * Absent or unrecognized falls through to the same skip
     * `UNRESOLVED_CONTENT_BLOCK_TYPES` performs — skipped, not guessed.
     */
    gamificationConfig?: {
        gamificationType?: string;
    };
}

/**
 * `SurfaceContentBlockType` -> `{role, footprint}`, grounded the same way
 * `COLLECTION_TYPES` is: read off the anchors and `FLUIDITY_CONTRACT.md` §7,
 * not invented per type.
 *
 * - COLLECTION/HORIZONTAL_RAIL — scannable sets of peer cards.
 * - FEATURE/COLUMN — the contested column's occupants (§7: "Leaderboard /
 *   standings — column occupant"; `mech--ad`'s cards share the column's
 *   `md--img` chrome alongside the live-creator and trailer cards).
 * - ENGAGEMENT/COLUMN_WIDGET — 392px column widgets (§7: "Journey quest",
 *   "Gamification (bingo)"). `CHALLENGE_ZONE`'s own doc — "scavenger hunt
 *   checklist, mini leaderboard, or bingo card" — is the bingo shape.
 * - STAGE — `STREAM_ZONE`'s own doc ("16:9 player") is the stage's own spec
 *   (§7: "player, `aspect-ratio:16/9`").
 * - STATEMENT/BAND — single full-width statements.
 */
const CONTENT_BLOCK_SLOT: Partial<Record<string, { role: SlotRole; footprint: SlotFootprint }>> = {
    // COLLECTION — scannable peer sets
    PROMO: {role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
    REWARDS: {role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
    PRODUCTS: {role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
    PRIZES: {role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
    IMAGE_GALLERY: {role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
    /*
     * BADGES ARE A BAND, not a rail — corrected against the anchor.
     *
     * `.bdg` (`rttf-drop-hub.html:738`) is a full-width bordered panel and
     * `.bdg__g` is `grid-template-columns: repeat(auto-fit, minmax(150px,1fr))`
     * — no `overflow-x`, no scroll-snap, no `flex: 0 0` children, which is
     * every property that makes a horizontal rail a rail.
     *
     * The distinction is not cosmetic. A rail says "move along these and there
     * are more"; a badge grid says "here is the whole set, and the gaps are
     * the ones you have not earned." Scrolling that hides how many are left,
     * which is the only thing it is there to show.
     */
    ACHIEVEMENTS: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    // FEATURE/COLUMN — the contested column
    LEADERBOARD: {role: SlotRole.FEATURE, footprint: SlotFootprint.COLUMN},
    PLAYABLE_AD: {role: SlotRole.FEATURE, footprint: SlotFootprint.COLUMN},
    // ENGAGEMENT/COLUMN_WIDGET
    QUEST: {role: SlotRole.ENGAGEMENT, footprint: SlotFootprint.COLUMN_WIDGET},
    CHALLENGE_ZONE: {role: SlotRole.ENGAGEMENT, footprint: SlotFootprint.COLUMN_WIDGET},
    // STAGE
    STREAM_ZONE: {role: SlotRole.STAGE, footprint: SlotFootprint.STAGE},
    // STATEMENT/BAND — single full-width statements
    HTML: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    TEXT_IMAGE: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    COUNTDOWN: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    SPONSORS: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    STEPS: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    FAQ: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    CTA_BANNER: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    VIDEO: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    RICH_TEXT: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    DIVIDER: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    STATS: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    LIVE_BRACKET: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    COMPETITION: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    MY_POSITION: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    SOCIAL_EMBED: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
    EARNING: {role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
};

/**
 * Deliberately unresolved — no anchor evidence and no obvious footprint, so
 * not guessed. Each carries the reason; a type stays here until an anchor or
 * a product decision resolves it, not until someone picks a footprint to make
 * the list shorter.
 *
 * - `CONTAINER`, `COLUMNS` — layout wrappers, not occupants; whether the
 *   mapper should recurse into their children is unresolved.
 * - `ONBOARDING` — its own doc says "overlay". `PRODUCT_PLAN.md` rule 2:
 *   overlays are a region of PAGE, never a structure — likely not a slot
 *   occupant at all, unconfirmed.
 * - `GAMIFICATION` — a passthrough to `GamificationRenderer`; its real
 *   footprint depends on which of the 26 `GamificationType` members it
 *   wraps, which needs its own mapping, not one footprint for all of them.
 * - `SCROLL_NAV`, `STORE_LOCATOR`, `CODE_ENTRY`, `NOTIFICATION_SIGNUP`,
 *   `POLL`, `LOOTBOX`, `BRAND_CHALLENGE`, `QUEST_SPONSORSHIP`,
 *   `GUILD_SPONSORSHIP` — no anchor in the corpus shows any of these; a
 *   footprint here would be invented, not read off anything.
 * - `STREAMING` as a content block (singular embed) is a different shape
 *   from the section-level `STREAMING` type `dropSlots()` handles above (a
 *   roster of streams, correctly a HORIZONTAL_RAIL) — no anchor evidence ties
 *   a single embedded-stream block to one footprint, so it is not assumed to
 *   share the section type's answer.
 */
export const UNRESOLVED_CONTENT_BLOCK_TYPES = new Set([
    "CONTAINER", "COLUMNS", "ONBOARDING", "GAMIFICATION", "SCROLL_NAV",
    "STORE_LOCATOR", "CODE_ENTRY", "NOTIFICATION_SIGNUP", "POLL", "LOOTBOX",
    "BRAND_CHALLENGE", "QUEST_SPONSORSHIP", "GUILD_SPONSORSHIP", "STREAMING",
]);

/**
 * Gamifications that render OUTSIDE the slot flow.
 *
 * Not the same thing as unresolved. An unresolved type is one whose footprint
 * nobody has decided; these have been decided and the answer is "no slot" —
 * they are chrome, positioned against the viewport rather than placed in the
 * page. Skipping them silently alongside the unknowns would lose that, and
 * the next reader would add a footprint for one and be wrong.
 *
 * WHEEL_SPIN is `position:fixed` bottom-right into a modal (anchor `:781`,
 * `.wfab` / `.whm`), which FLUIDITY_CONTRACT §7 states outright: "floating
 * prompt (FAB) -> modal, NOT a card in a rail."
 */
export const UNPLACED_GAMIFICATION_TYPES = new Set(["WHEEL_SPIN"]);

/**
 * `GamificationType` -> `{role, footprint}` — the SUB-routing a `GAMIFICATION`
 * block needs, because the block's own type cannot answer where it goes.
 *
 * `SurfaceGamificationConfig` carries only a `gamificationModelId`, so every
 * gamification block looks identical at this layer. A wheel, a streak and a
 * prediction are three different shapes, and the only thing that distinguishes
 * them is the referenced model's `type` — which the caller resolves and hands
 * in as `gamificationType`.
 *
 * Every entry below is READ OFF THE ANCHOR, not chosen here. A type with no
 * row is skipped rather than guessed, on the same contract the unresolved set
 * keeps: a wrong footprint renders and looks deliberate, which is worse than
 * an absent one.
 *
 * This is DATA. A new placement is a row; it is never a case in a renderer,
 * and no campaign's name appears in it.
 */
const GAMIFICATION_SLOT: Partial<Record<string, { role: SlotRole; footprint: SlotFootprint }>> = {
    /*
     * A prediction reads DOWN — who you backed, when it locks, what it pays,
     * how to change it — so it takes the column widget beside the rails
     * (anchor `:1293`: `float:right; width:360px`). Not a band: a band would
     * leave two thirds of a row empty, and inventing content for that gap is
     * the tell that the block is the wrong width.
     *
     * The finalist DECK is a separate slot, not a second footprint of this
     * type. The deck is a collection of cards; predictions render the pick
     * affordance onto them, and the roster never stores a pick.
     */
    PREDICTIONS: {role: SlotRole.ENGAGEMENT, footprint: SlotFootprint.COLUMN_WIDGET},

    /*
     * One streak, and it is a CARD IN THE STRIP under the stage — anchor
     * `:1859`, `.fs.fs--stk`, the first occupant of `.fstrip`. Not a section
     * of its own: a streak is one number and a mark, and giving it a band
     * makes the page argue with itself about how important it is.
     */
    STREAK_BOARD: {role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
};


/**
 * Build the slot list for a surface's content blocks (`Drop.contentBlocks[]`
 * and the `Hub`-family blocks that share `SurfaceContentBlockType`).
 *
 * An unresolved type (`UNRESOLVED_CONTENT_BLOCK_TYPES`) is skipped, not
 * guessed — absence is a configuration the same way an un-authored section
 * is, per `slots.ts` rule 3. It does not appear as a broken slot; it appears
 * as nothing, which is the correct rendering of "we don't know where this
 * goes yet" until it does.
 */
export function contentBlockSlots(blocks: readonly MappableContentBlock[] | null | undefined): SurfaceSlot[] {
    const live = (blocks ?? []).filter(b => b.enabled !== false);
    const slots: SurfaceSlot[] = [];

    live.forEach((b, i) => {
        const type = b.type ?? "";
        /*
         * GAMIFICATION resolves by its SUB-type, not its block type — see
         * `GAMIFICATION_SLOT`. A type with no row, and one that is placed
         * outside the flow entirely, both fall through to the same skip the
         * unresolved set already performs.
         */
        const mapped = type === "GAMIFICATION"
            ? GAMIFICATION_SLOT[b.gamificationConfig?.gamificationType ?? ""]
            : CONTENT_BLOCK_SLOT[type];
        if (!mapped) return;

        slots.push({
            id: b._id ?? b.id ?? `block-${i}`,
            role: mapped.role,
            footprint: mapped.footprint,
            order: b.order ?? i + 1,
        });
    });

    return slots;
}
